import React, { useState } from "react";
import {
  Alert,
  Box,
  Backdrop,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Avatar,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { handleUserLogin } from "../../redux/reducers/authReducer";
import { unwrapResult } from "@reduxjs/toolkit";
import Loading from "../utilities/Loading";
import axiosInstance from "../../helpers/AxiosInstance";
import { useNavigate } from "react-router-dom";
import i18next from "../../helpers/i18n";

const ModernContainer = styled(Box)`
  min-height: 100vh;
  background-color: #f4f4f4;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
  overflow: hidden;

  /* Hero Image - Top Right */
  &::before {
    content: "";
    position: absolute;
    top: 5%;
    right: 5%;
    width: 300px;
    height: 300px;
    background: url("/assets/images/hero.jpeg") no-repeat center;
    background-size: contain;
    z-index: 0;
    opacity: 0.8;
  }

  /* Hero Image - Bottom Left */
  &::after {
    content: "";
    position: absolute;
    bottom: 5%;
    left: 5%;
    width: 250px;
    height: 250px;
    background: url("/assets/images/hero.jpeg") no-repeat center;
    background-size: contain;
    z-index: 0;
    opacity: 0.6;
  }
`;

const LoginCard = styled(Paper)`
  width: 100%;
  max-width: 600px;
  border-radius: 4px;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.15);
  background: #ffffff;
  position: relative;
  z-index: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const FormPadding = styled(Box)`
  padding: 50px 50px 30px 50px;
  @media (max-width: 600px) {
    padding: 30px 20px 20px 20px;
  }
`;

const LogoContainer = styled(Box)`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const StyledAvatar = styled(Avatar)`
  width: 120px;
  height: 120px;
  border-radius: 24px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  border: 4px solid #ffffff;
  background: white;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const Title = styled(Typography)`
  font-size: 24px;
  font-weight: 300;
  color: #555555;
  margin-bottom: 40px;
  text-align: left;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const Subtitle = styled(Typography)`
  font-size: 15px;
  color: #64748b;
  margin-bottom: 32px;
  text-align: center;
  font-weight: 400;
`;

// Removed default TabContainer and StyledTabs as they are no longer used based on the image format

const FormSection = styled(Box)`
  margin-bottom: 24px;
  background: #ffffff;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
`;

const SectionTitle = styled(Typography)`
  font-size: 15px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  &::before {
    display: none;
  }
`;

const StyledTextField = styled(TextField)`
  & .MuiInput-underline:before {
    border-bottom: 1px solid #e0e0e0;
  }
  & .MuiInput-underline:hover:not(.Mui-disabled):before {
    border-bottom: 1px solid #b0b0b0;
  }
  & .MuiInput-underline:after {
    border-bottom: 2px solid #324056;
  }

  & .MuiInputLabel-root {
    color: #888888;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;

    &.Mui-focused {
      color: #324056;
    }
  }

  & .MuiInputBase-input {
    padding-bottom: 8px;
    font-size: 16px;
    color: #333333;
  }
`;

// We'll replace this with action buttons layout.
const ActionRow = styled(Box)`
  display: flex;
  width: 100%;
  height: 60px;
`;

const ActionButton = styled(Button)`
  flex: 1;
  border-radius: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;

  &.left-btn {
    background: #e9eaec;
    color: #888ba0;
    &:hover {
      background: #dbdcde;
    }
  }

  &.right-btn {
    background: #1f4f5c;
    color: white;
    &:hover {
      background: #252e3e;
    }
  }

  &:disabled {
    background: #cbd5e1;
    color: #f8fafc;
  }
`;

const ForgotPasswordLink = styled(Button)`
  color: #888ba0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-top: 30px;
  &:hover {
    background: transparent;
    color: #555555;
  }
`;

const DividerContainer = styled(Box)`
  margin: 24px 0;
  display: flex;
  align-items: center;
`;

const StyledDivider = styled(Divider)`
  flex: 1;
  border-color: #e2e8f0;
`;

const DividerText = styled(Typography)`
  padding: 0 16px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
`;

export default function ModernLogin() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { companyData } = useSelector((state) => ({
    companyData: state.Auth.companyData,
  }));
  const { t } = useTranslation("auth");
  const isArabic = i18next.language?.toLowerCase().startsWith("ar");

  // Login schema
  const loginSchema = yup.object({
    username: yup.string().required("Username is required"),
    password: yup.string().required("Password is required"),
  });

  // Registration schema with only essential required fields
  const registrationSchema = yup.object({
    clientName: yup.string().required("Client name is required"),
    cityId: yup.number().optional(),
    address1: yup.string().required("Address 1 is required"),
    address2: yup.string().optional(),
    contactPerson: yup.string().optional(),
    contactNo1: yup.string().required("Contact number 1 is required"),
    contactNo2: yup.string().optional(),
    email: yup.string().email("Invalid email").optional(),
    monthlyAwbs: yup.number().optional(),
    bankName: yup.string().optional(),
    bankAccount: yup.string().optional(),
    allowToOpenShipment: yup.boolean().optional(),
    notes: yup.string().optional(),
  });

  const loginForm = useForm({
    mode: "onTouched",
    defaultValues: {
      username: "",
      password: "",
    },
    resolver: yupResolver(loginSchema),
  });

  const registrationForm = useForm({
    mode: "onTouched",
    defaultValues: {
      clientName: "",
      cityId: 1,
      address1: "",
      address2: "",
      contactPerson: "",
      contactNo1: "",
      contactNo2: "",
      email: "",
      monthlyAwbs: 0,
      bankName: "",
      bankAccount: "",
      allowToOpenShipment: false,
      notes: "",
    },
    resolver: yupResolver(registrationSchema),
  });

  const {
    handleSubmit: handleLoginSubmit,
    register: loginRegister,
    formState: { errors: loginErrors },
  } = loginForm;
  const {
    handleSubmit: handleRegistrationSubmit,
    register: registrationRegister,
    formState: { errors: registrationErrors },
  } = registrationForm;

  const onLogin = (data) => {
    setLoading(true);
    setError("");

    dispatch(
      handleUserLogin({
        data: data,
        setLoading: setLoading,
      }),
    )
      .then(unwrapResult)
      .catch((err) => {
        setLoading(false);
        setError("Login failed. Please check your credentials.");
      });
  };

  const onRegistration = async (data) => {
    setLoading(true);
    setError("");

    console.log("Registration data:", data);

    try {
      const instance = axiosInstance();
      const response = await instance.post(
        "/Free/V6/Register",
        {
          clientName: data.clientName,
          cityID: parseInt(data.cityId),
          address1: data.address1,
          address2: data.address2 || "",
          contactPerson: data.contactPerson,
          contactNo1: data.contactNo1,
          contactNo2: data.contactNo2 || "",
          email: data.email,
          monthlyAWBs: parseInt(data.monthlyAwbs) || 0,
          bankName: data.bankName || "",
          bankAccount: data.bankAccount || "",
          allowToOpenShipment:
            data.allowToOpenShipment === "true" ||
            data.allowToOpenShipment === true,
          notes: data.notes || "",
        },
        {
          headers: {
            CompanyID: "280533",
          },
        },
      );

      setLoading(false);

      // If the API returns a success message or similar
      if (
        (response.data && response.data.hasError === false) ||
        !response.data?.hasError
      ) {
        setError("");
        const msg = isArabic
          ? "تم تسجيل حسابك بنجاح. سيتم إرسال اسم المستخدم وكلمة المرور على بريدك الإلكتروني في أقرب وقت."
          : "Your account has been registered successfully. You will receive your username and password via email shortly.";
        setSuccessMsg(msg);
        setActiveTab(0);
        registrationForm.reset();
      } else {
        setError(
          response.data?.message || "Registration failed. Please try again.",
        );
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      setError(
        err.response?.data?.message ||
          "An error occurred during registration. Please try again.",
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError("");
  };

  return (
    <ModernContainer>
      {loading && (
        <Backdrop sx={{ color: "#fff", zIndex: 24 }} open={true}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {/* Hero images are now using CSS pseudo-elements */}

      <LoginCard elevation={0}>
        <FormPadding>
          {/* Default Company Logo hidden in this layout, but could be added back if required */}

          <Title variant="h4">{activeTab === 0 ? "LOGIN" : "REGISTER"}</Title>

          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "#fee2e2", borderRadius: 1 }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          {activeTab === 0 ? (
            // Login Form
            <form id="login-form" onSubmit={handleLoginSubmit(onLogin)}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    label={t("Login.UserName_Label")}
                    placeholder=""
                    {...loginRegister("username")}
                    error={!!loginErrors.username}
                    helperText={loginErrors.username?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    type="password"
                    label={t("Login.Password_Label")}
                    placeholder=""
                    {...loginRegister("password")}
                    error={!!loginErrors.password}
                    helperText={loginErrors.password?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  mt: 2,
                }}
              >
                <ForgotPasswordLink
                  disableRipple
                  onClick={() => navigate("/auth/forgot-password")}
                >
                  FORGOT YOUR PASSWORD?
                </ForgotPasswordLink>
              </Box>
            </form>
          ) : (
            // Registration Form - Simplified layout
            <form
              id="register-form"
              onSubmit={handleRegistrationSubmit(onRegistration)}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    label={`${t("Login.Client_Name")} *`}
                    placeholder=""
                    {...registrationRegister("clientName")}
                    error={!!registrationErrors.clientName}
                    helperText={registrationErrors.clientName?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    label={t("Login.Primary_Address")}
                    placeholder=""
                    {...registrationRegister("address1")}
                    error={!!registrationErrors.address1}
                    helperText={registrationErrors.address1?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    label={t("Login.Contact_Person")}
                    placeholder=""
                    {...registrationRegister("contactPerson")}
                    error={!!registrationErrors.contactPerson}
                    helperText={registrationErrors.contactPerson?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    label={`${t("Login.Primary_Phone")} *`}
                    placeholder=""
                    {...registrationRegister("contactNo1")}
                    error={!!registrationErrors.contactNo1}
                    helperText={registrationErrors.contactNo1?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    variant="standard"
                    type="email"
                    label={t("Login.Email_Address")}
                    placeholder=""
                    {...registrationRegister("email")}
                    error={!!registrationErrors.email}
                    helperText={registrationErrors.email?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {/* Terms and Conditions Checkbox */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    mb: 1,
                    fontSize: "13px",
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {isArabic
                    ? "بإنشاء حساب، فإنك توافق على الشروط والأحكام الخاصة بنا."
                    : "By creating an account, you agree to our Terms and Conditions."}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexDirection: isArabic ? "row-reverse" : "row",
                  }}
                >
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="terms-checkbox"
                    style={{
                      fontSize: "14px",
                      color: "#334155",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {isArabic
                      ? "أوافق على الشروط والأحكام"
                      : "I agree to the Terms and Conditions"}
                  </label>
                </Box>
              </Box>
            </form>
          )}
        </FormPadding>

        {/* Footer Action Buttons matching the image */}
        <ActionRow>
          <ActionButton
            className="left-btn"
            onClick={() => {
              if (activeTab === 0) {
                setActiveTab(1); // switch to register
              } else {
                setActiveTab(0); // switch to login
              }
            }}
          >
            {activeTab === 0 ? "REGISTER" : "LOGIN"}
          </ActionButton>

          <ActionButton
            className="right-btn"
            type="submit"
            form={activeTab === 0 ? "login-form" : "register-form"}
            disabled={loading || (activeTab === 1 && !agreedToTerms)}
          >
            {loading ? (
              <Loading
                isButtonLoading={true}
                buttonText={activeTab === 0 ? "SIGN IN" : "SUBMIT"}
                spinnerType="2"
              />
            ) : activeTab === 0 ? (
              "SIGN IN"
            ) : (
              "SUBMIT"
            )}
          </ActionButton>
        </ActionRow>

        <Snackbar
          open={!!successMsg}
          autoHideDuration={6000}
          onClose={() => setSuccessMsg("")}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessMsg("")}
            severity="success"
            sx={{ width: "100%" }}
          >
            {successMsg}
          </Alert>
        </Snackbar>
      </LoginCard>
    </ModernContainer>
  );
}
